'use client'

import {
  FindingList,
  MatchQualityBadge,
  ScoreRing,
  Card,
  CardContent,
  MARKETPLACE_INSIGHT_CAPTION,
  FIT_ANALYSIS_COPY,
  cn,
} from '@yeshuman/sdk'
import type { ShortlistInsight } from '@yeshuman/sdk'
import { Info, Loader2 } from 'lucide-react'
import { AnalysisRetryButton } from '../search/analysis-retry-button'

export type CandidateJobFitSummaryWidgetProps = {
  insight: ShortlistInsight | null
  pending?: boolean
  stuck?: boolean
  onRetry?: () => void
  retryPending?: boolean
  className?: string
}

/** Gold edge only — keep the card body readable (no wash). */
const fitSurfaceClass = 'border border-[var(--marketing-gold,#f6c94c)]/60 bg-card'

/** Compact fit strip for the job page: score + band, then full-width summary and notes. */
export function CandidateJobFitSummaryWidget({
  insight,
  pending = false,
  stuck = false,
  onRetry,
  retryPending = false,
  className,
}: CandidateJobFitSummaryWidgetProps) {
  if (stuck) {
    return (
      <Card className={cn(fitSurfaceClass, className)}>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-0">
          <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
            {FIT_ANALYSIS_COPY.stuck}
          </p>
          {onRetry ? (
            <AnalysisRetryButton mode="try" onClick={onRetry} pending={retryPending} />
          ) : null}
        </CardContent>
      </Card>
    )
  }

  if (pending) {
    return (
      <Card className={cn(fitSurfaceClass, className)}>
        <CardContent className="flex items-center gap-4 p-0">
          <Loader2 className="h-6 w-6 shrink-0 animate-spin text-muted-foreground" aria-hidden />
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Your fit
            </p>
            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
              {FIT_ANALYSIS_COPY.candidatePending}
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!insight) {
    return (
      <Card className={cn('border-dashed bg-muted/20', className)}>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-0">
          <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
            {FIT_ANALYSIS_COPY.missing}
          </p>
          {onRetry ? (
            <AnalysisRetryButton mode="try" onClick={onRetry} pending={retryPending} />
          ) : null}
        </CardContent>
      </Card>
    )
  }

  const score = Math.max(0, Math.min(1, (insight.match_percent ?? 0) / 100))
  const summary = insight.summary?.trim() || insight.recommendation?.trim() || ''
  const hasNotes = insight.highlights.length > 0 || insight.red_flags.length > 0

  return (
    <Card className={cn(fitSurfaceClass, className)}>
      <CardContent className="space-y-3 p-0">
        <div className="flex items-center gap-4">
          <ScoreRing score={score} size={72} caption="match" className="shrink-0" />
          <div className="min-w-0 space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Your fit
            </p>
            <MatchQualityBadge band={insight.band} score={score} showPercent={false} />
          </div>
        </div>

        {hasNotes ? (
          <div className="space-y-1.5">
            <FindingList
              items={insight.highlights}
              tone="success"
              max={2}
              marker="thumbs"
            />
            <FindingList
              items={insight.red_flags}
              tone="warning"
              max={2}
              marker="thumbs"
            />
            {insight.source === 'listing_description' ? (
              <p className="flex items-start gap-1.5 pt-0.5 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{MARKETPLACE_INSIGHT_CAPTION}</span>
              </p>
            ) : null}
          </div>
        ) : null}

        {summary ? (
          <p
            className={cn(
              'text-sm leading-relaxed text-muted-foreground',
              hasNotes && 'border-t border-border/50 pt-3',
            )}
          >
            {summary}
          </p>
        ) : null}

        {onRetry ? (
          <div className="flex justify-end">
            <AnalysisRetryButton mode="run" onClick={onRetry} pending={retryPending} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
